/**
 * useVideo Composable
 *
 * Comprehensive local media management for the Canasta game.
 * Handles camera and microphone access with security best practices.
 *
 * Features:
 * - WebRTC getUserMedia for camera/microphone access
 * - Reactive state tracking (videoEnabled, audioEnabled, permissionState)
 * - HTTPS security checks (required for camera access)
 * - Comprehensive error handling with user-friendly messages
 * - Track ended event monitoring
 * - Complete hardware release when disabled (turns off camera light)
 * - Audio settings: echo cancellation, noise suppression, auto gain control
 * - Browser permission state detection
 * - LocalStorage settings persistence
 *
 * Security:
 * - Requires HTTPS or localhost for camera access
 * - Complete track.stop() to release hardware
 * - Permission state detection and helpful error messages
 * - No silent recording - user must explicitly enable
 *
 * @example
 * const { videoEnabled, audioEnabled, requestAccess, toggleVideo } = useVideo()
 * await requestAccess()  // Request permissions
 * await toggleVideo()    // Enable camera
 * await toggleAudio()    // Enable microphone
 */

import { ref, computed, watch, onMounted, onUnmounted } from 'vue'
import { Logger, LogCategory } from '@/utils/Logger.class'

/**
 * Media constraints for getUserMedia
 */
interface MediaConstraints {
  video: {
    width: { ideal: number }
    height: { ideal: number }
    facingMode: string
  }
  audio: {
    echoCancellation: boolean
    noiseSuppression: boolean
    autoGainControl: boolean
  }
}

/**
 * Permission states
 */
type PermissionState = 'prompt' | 'granted' | 'denied'

/**
 * Session storage keys for video state persistence across refreshes
 */
const SESSION_VIDEO_KEY = 'canasta_video_enabled'
const SESSION_AUDIO_KEY = 'canasta_audio_enabled'

/**
 * Reactive state (global, shared across all useVideo instances)
 */
const videoEnabled = ref(false)
const audioEnabled = ref(false)
const permissionState = ref<PermissionState>('prompt')
const localStream = ref<MediaStream | null>(null)
let videoTrack: MediaStreamTrack | null = null
let audioTrack: MediaStreamTrack | null = null

/**
 * Check if video was enabled before page refresh (for auto-restore)
 */
export function getSessionVideoPreference(): { video: boolean; audio: boolean } {
  return {
    video: sessionStorage.getItem(SESSION_VIDEO_KEY) === 'true',
    audio: sessionStorage.getItem(SESSION_AUDIO_KEY) === 'true',
  }
}

/**
 * Clear session video preferences (on explicit disable or game leave)
 */
export function clearSessionVideoPreference(): void {
  sessionStorage.removeItem(SESSION_VIDEO_KEY)
  sessionStorage.removeItem(SESSION_AUDIO_KEY)
}

/**
 * Media constraints optimized for game video chat
 */
const CONSTRAINTS: MediaConstraints = {
  video: {
    width: { ideal: 320 },
    height: { ideal: 240 },
    facingMode: 'user',
  },
  audio: {
    echoCancellation: true,
    noiseSuppression: true,
    autoGainControl: true,
  },
}

/**
 * Scoped loggers for video/audio operations (lazy-initialized to avoid circular dependency)
 */
let _videoLog: ReturnType<typeof Logger.scope> | null = null
let _audioLog: ReturnType<typeof Logger.scope> | null = null

function getVideoLog() {
  if (!_videoLog) {
    _videoLog = Logger.scope(LogCategory.VIDEO)
  }
  return _videoLog
}

function getAudioLog() {
  if (!_audioLog) {
    _audioLog = Logger.scope(LogCategory.AUDIO)
  }
  return _audioLog
}

/**
 * Check if browser supports getUserMedia
 */
function isSupported(): boolean {
  return !!(navigator.mediaDevices && navigator.mediaDevices.getUserMedia)
}

/**
 * Check if running in secure context (HTTPS or localhost)
 */
function isSecureContext(): boolean {
  return (
    window.isSecureContext ||
    window.location.protocol === 'https:' ||
    window.location.hostname === 'localhost' ||
    window.location.hostname === '127.0.0.1'
  )
}

/**
 * useVideo Composable
 */
export function useVideo() {
  // Load settings from localStorage
  onMounted(() => {
    const saved = localStorage.getItem('videoSettings')
    if (saved) {
      try {
        const _settings = JSON.parse(saved)
        // Don't auto-enable, just track preferences
        // User must explicitly enable due to browser autoplay policies
        void _settings // Acknowledge parsing for now
      } catch (e) {
        getVideoLog().warn('Failed to load video settings', e)
      }
    }
  })

  /**
   * Save settings to localStorage
   */
  function saveSettings(): void {
    localStorage.setItem(
      'videoSettings',
      JSON.stringify({
        videoEnabled: videoEnabled.value,
        audioEnabled: audioEnabled.value,
      })
    )
  }

  /**
   * Watch for state changes and save to localStorage
   */
  watch([videoEnabled, audioEnabled], () => {
    saveSettings()
  })

  /**
   * Request media access (camera + microphone)
   */
  async function requestAccess(): Promise<boolean> {
    getVideoLog().info('requestAccess() called')

    if (!isSupported()) {
      getVideoLog().warn('getUserMedia not supported in this browser')
      return false
    }

    getVideoLog().debug('Requesting media with constraints', CONSTRAINTS)

    try {
      const stream = await navigator.mediaDevices.getUserMedia(CONSTRAINTS)

      videoTrack = stream.getVideoTracks()[0] || null
      audioTrack = stream.getAudioTracks()[0] || null

      getVideoLog().info('Media access granted', {
        videoTracks: stream.getVideoTracks().length,
        audioTracks: stream.getAudioTracks().length
      })

      if (videoTrack) {
        getVideoLog().debug('Video track acquired', {
          id: videoTrack.id,
          label: videoTrack.label,
          enabled: videoTrack.enabled,
          readyState: videoTrack.readyState
        })
      }

      if (audioTrack) {
        getAudioLog().debug('Audio track acquired', {
          id: audioTrack.id,
          label: audioTrack.label,
          enabled: audioTrack.enabled,
          readyState: audioTrack.readyState
        })
      }

      // Initially disable tracks (user must explicitly enable)
      if (videoTrack) videoTrack.enabled = false
      if (audioTrack) audioTrack.enabled = false

      // Listen for track ended events
      if (videoTrack) {
        videoTrack.onended = () => {
          getVideoLog().warn('Video track ended unexpectedly', { trackId: videoTrack?.id })
          videoEnabled.value = false
          videoTrack = null
        }
      }

      if (audioTrack) {
        audioTrack.onended = () => {
          getAudioLog().warn('Audio track ended unexpectedly', { trackId: audioTrack?.id })
          audioEnabled.value = false
          audioTrack = null
        }
      }

      localStream.value = stream
      permissionState.value = 'granted'

      return true
    } catch (error: any) {
      getVideoLog().error('Error accessing media', {
        name: error.name,
        message: error.message,
        constraint: error.constraint
      })

      if (error.name === 'NotAllowedError' || error.name === 'PermissionDeniedError') {
        getVideoLog().warn('Permission denied - user blocked camera/microphone access')
        permissionState.value = 'denied'
      }

      return false
    }
  }

  /**
   * Enable video
   */
  async function enableVideo(): Promise<boolean> {
    getVideoLog().info('enableVideo() called', { currentState: videoEnabled.value })

    if (!isSecureContext()) {
      getVideoLog().warn('Camera requires HTTPS or localhost', {
        protocol: window.location.protocol,
        hostname: window.location.hostname
      })
      alert('Camera requires HTTPS or localhost')
      return false
    }

    // Request new track if needed
    if (!videoTrack) {
      getVideoLog().debug('No existing video track, requesting new one')
      try {
        // VIDEO-1: Before getUserMedia
        getVideoLog().info('[VIDEO-1] Calling getUserMedia', { constraints: CONSTRAINTS.video })
        const stream = await navigator.mediaDevices.getUserMedia({ video: CONSTRAINTS.video })
        videoTrack = stream.getVideoTracks()[0] ?? null

        if (videoTrack) {
          // VIDEO-2: After track acquired
          getVideoLog().info('[VIDEO-2] Video track acquired', {
            trackId: videoTrack.id,
            label: videoTrack.label,
            readyState: videoTrack.readyState,
            enabled: videoTrack.enabled
          })
        }

        if (localStream.value && videoTrack) {
          localStream.value.addTrack(videoTrack)
          getVideoLog().debug('Added video track to existing stream')
        } else {
          localStream.value = stream
          getVideoLog().debug('Created new stream with video track')
        }

        if (videoTrack) {
          videoTrack.onended = () => {
            getVideoLog().warn('Video track ended unexpectedly', { trackId: videoTrack?.id })
            videoEnabled.value = false
            videoTrack = null
          }
        }

        permissionState.value = 'granted'
      } catch (error: any) {
        getVideoLog().error('Error enabling video', {
          name: error.name,
          message: error.message
        })
        if (error.name === 'NotAllowedError') {
          permissionState.value = 'denied'
        }
        return false
      }
    }

    if (videoTrack) {
      videoTrack.enabled = true
      videoEnabled.value = true
      // Save to session storage for auto-restore after refresh
      sessionStorage.setItem(SESSION_VIDEO_KEY, 'true')
      // VIDEO-3: After videoEnabled set
      getVideoLog().info('[VIDEO-3] videoEnabled set to true', {
        trackId: videoTrack.id,
        hasStream: !!localStream.value,
        streamTracks: localStream.value?.getTracks().length
      })
      return true
    }

    getVideoLog().warn('enableVideo() failed - no video track available')
    return false
  }

  /**
   * Disable video - releases camera hardware
   */
  function disableVideo(): void {
    getVideoLog().info('disableVideo() called')

    if (videoTrack) {
      getVideoLog().debug('Stopping video track', { id: videoTrack.id })
      videoTrack.stop()
      videoTrack = null
    }

    if (localStream.value) {
      const tracks = localStream.value.getVideoTracks()
      getVideoLog().debug('Removing video tracks from stream', { count: tracks.length })
      tracks.forEach((track) => {
        localStream.value!.removeTrack(track)
        track.stop()
      })
    }

    videoEnabled.value = false
    // Clear session storage - user explicitly disabled
    sessionStorage.removeItem(SESSION_VIDEO_KEY)
    getVideoLog().info('Video disabled and hardware released')
  }

  /**
   * Enable audio
   */
  async function enableAudio(): Promise<boolean> {
    getAudioLog().info('enableAudio() called', { currentState: audioEnabled.value })

    // Request new track if needed
    if (!audioTrack) {
      getAudioLog().debug('No existing audio track, requesting new one')
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: CONSTRAINTS.audio })
        audioTrack = stream.getAudioTracks()[0] ?? null

        if (audioTrack) {
          getAudioLog().info('New audio track acquired', {
            id: audioTrack.id,
            label: audioTrack.label,
            readyState: audioTrack.readyState
          })
        }

        if (localStream.value && audioTrack) {
          localStream.value.addTrack(audioTrack)
          getAudioLog().debug('Added audio track to existing stream')
        } else {
          localStream.value = stream
          getAudioLog().debug('Created new stream with audio track')
        }

        if (audioTrack) {
          audioTrack.onended = () => {
            getAudioLog().warn('Audio track ended unexpectedly', { trackId: audioTrack?.id })
            audioEnabled.value = false
            audioTrack = null
          }
        }

        permissionState.value = 'granted'
      } catch (error: any) {
        getAudioLog().error('Error enabling audio', {
          name: error.name,
          message: error.message
        })
        return false
      }
    }

    if (audioTrack) {
      audioTrack.enabled = true
      audioEnabled.value = true
      // Save to session storage for auto-restore after refresh
      sessionStorage.setItem(SESSION_AUDIO_KEY, 'true')
      getAudioLog().info('Audio enabled successfully', { trackId: audioTrack.id })
      return true
    }

    getAudioLog().warn('enableAudio() failed - no audio track available')
    return false
  }

  /**
   * Disable audio - releases microphone hardware
   */
  function disableAudio(): void {
    getAudioLog().info('disableAudio() called')

    if (audioTrack) {
      getAudioLog().debug('Stopping audio track', { id: audioTrack.id })
      audioTrack.stop()
      audioTrack = null
    }

    if (localStream.value) {
      const tracks = localStream.value.getAudioTracks()
      getAudioLog().debug('Removing audio tracks from stream', { count: tracks.length })
      tracks.forEach((track) => {
        localStream.value!.removeTrack(track)
        track.stop()
      })
    }

    audioEnabled.value = false
    // Clear session storage - user explicitly disabled
    sessionStorage.removeItem(SESSION_AUDIO_KEY)
    getAudioLog().info('Audio disabled and hardware released')
  }

  /**
   * Toggle video on/off
   */
  async function toggleVideo(): Promise<boolean> {
    getVideoLog().info('toggleVideo() called', { currentState: videoEnabled.value })
    if (videoEnabled.value) {
      disableVideo()
      return false
    } else {
      const result = await enableVideo()
      getVideoLog().info('toggleVideo() result', { success: result, newState: videoEnabled.value })
      return result
    }
  }

  /**
   * Toggle audio on/off
   */
  async function toggleAudio(): Promise<boolean> {
    getAudioLog().info('toggleAudio() called', { currentState: audioEnabled.value })
    if (audioEnabled.value) {
      disableAudio()
      return false
    } else {
      const result = await enableAudio()
      getAudioLog().info('toggleAudio() result', { success: result, newState: audioEnabled.value })
      return result
    }
  }

  /**
   * Check permissions (if browser supports it)
   */
  async function checkPermissions(): Promise<PermissionState> {
    if (navigator.permissions && navigator.permissions.query) {
      try {
        const result = await navigator.permissions.query({ name: 'camera' as PermissionName })
        return result.state as PermissionState
      } catch (e) {
        // Not all browsers support querying camera permission
        return 'prompt'
      }
    }
    return 'prompt'
  }

  /**
   * Get available media devices
   */
  async function getDevices(): Promise<MediaDeviceInfo[]> {
    if (!isSupported()) return []

    try {
      const devices = await navigator.mediaDevices.enumerateDevices()
      getVideoLog().debug('Enumerated devices', {
        total: devices.length,
        videoinput: devices.filter(d => d.kind === 'videoinput').length,
        audioinput: devices.filter(d => d.kind === 'audioinput').length,
        audiooutput: devices.filter(d => d.kind === 'audiooutput').length
      })
      return devices
    } catch (e) {
      getVideoLog().error('Failed to enumerate devices', e)
      return []
    }
  }

  /**
   * Stop all media and cleanup
   */
  function stopLocalMedia(): void {
    getVideoLog().info('stopLocalMedia() called - cleaning up all media')
    disableVideo()
    disableAudio()

    if (localStream.value) {
      const trackCount = localStream.value.getTracks().length
      localStream.value.getTracks().forEach((track) => track.stop())
      localStream.value = null
      getVideoLog().debug('Stopped all tracks and cleared stream', { trackCount })
    }
  }

  /**
   * Log current video state for debugging
   */
  function logVideoState(): void {
    getVideoLog().info('Video state snapshot', {
      videoEnabled: videoEnabled.value,
      audioEnabled: audioEnabled.value,
      permissionState: permissionState.value,
      hasLocalStream: !!localStream.value,
      hasVideoTrack: !!videoTrack,
      hasAudioTrack: !!audioTrack,
      videoTrackState: videoTrack ? {
        id: videoTrack.id,
        label: videoTrack.label,
        enabled: videoTrack.enabled,
        readyState: videoTrack.readyState,
        muted: videoTrack.muted
      } : null,
      audioTrackState: audioTrack ? {
        id: audioTrack.id,
        label: audioTrack.label,
        enabled: audioTrack.enabled,
        readyState: audioTrack.readyState,
        muted: audioTrack.muted
      } : null,
      streamTracks: localStream.value?.getTracks().map(t => ({
        kind: t.kind,
        id: t.id,
        enabled: t.enabled,
        readyState: t.readyState
      })) ?? []
    })
  }

  // Cleanup on unmount
  onUnmounted(() => {
    stopLocalMedia()
  })

  return {
    // Reactive state
    videoEnabled,
    audioEnabled,
    permissionState,
    localStream,
    isSupported: computed(() => isSupported()),

    // Media controls
    requestAccess,
    requestMediaAccess: requestAccess,  // Alias for backwards compatibility
    toggleVideo,
    toggleAudio,
    enableVideo,
    disableVideo,
    enableAudio,
    disableAudio,
    stopLocalMedia,

    // Utilities
    checkPermissions,
    getDevices,

    // Debug
    logVideoState,
  }
}
